import React from 'react';
import { X, Zap, Mail, Calendar, Users, BarChart3, Settings, Check, Bell, Info, Eye, Send, Copy } from 'lucide-react';
import { NotificationRecord } from '@/services/PagesServices/MarketingAndNotificationService';
import './NotificationModal.css';

interface NotificationModalProps {
  notification: NotificationRecord | null;
  onClose: () => void;
}

const NotificationModal: React.FC<NotificationModalProps> = ({ notification, onClose }) => {
  if (!notification) return null;

  const getTypeLabel = (type: string) => {
    if (type === 'email') return 'Email';
    if (type === 'in-app') return 'In-App';
    return 'Both';
  };

  return (
    <div className="trainer-marketing-modal-overlay" onClick={onClose}>
      <div className="trainer-marketing-modal" onClick={(e) => e.stopPropagation()}>
        <div className="trainer-marketing-modal-header">
          <div className="trainer-marketing-modal-title-row">
            <div className="trainer-marketing-modal-icon">
              <Zap size={24} />
            </div>
            <div>
              <h2 className="trainer-marketing-modal-title">{notification.title}</h2>
              <div className="trainer-marketing-modal-meta">
                <span className="trainer-marketing-modal-status">{notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}</span>
                <span>•</span>
                <span className="trainer-marketing-modal-type">{getTypeLabel(notification.type)}</span>
              </div>
            </div>
          </div>
          <button className="trainer-marketing-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="trainer-marketing-modal-body">
          <div className="trainer-marketing-modal-section">
            <div className="trainer-marketing-modal-section-header">
              <Mail size={16} className="trainer-marketing-section-icon" />
              <h3>Message Content</h3>
            </div>
            <div className="trainer-marketing-modal-message">
              {notification.message}
            </div>
          </div>

          <div className="trainer-marketing-modal-row">
            <div className="trainer-marketing-modal-section trainer-marketing-modal-half">
              <div className="trainer-marketing-modal-section-header">
                <Calendar size={16} className="trainer-marketing-section-icon" />
                <h3>Date & Time</h3>
              </div>
              <div className="trainer-marketing-date-info">
                <Check size={16} className="trainer-marketing-check-icon" />
                <div>
                  <div className="trainer-marketing-date-label-small">Sent Date</div>
                  <div className="trainer-marketing-date-value">{notification.sentDate || '-'}</div>
                </div>
              </div>
            </div>

            <div className="trainer-marketing-modal-section trainer-marketing-modal-half">
              <div className="trainer-marketing-modal-section-header">
                <Users size={16} className="trainer-marketing-section-icon" />
                <h3>Target Audience</h3>
              </div>
              <div className="trainer-marketing-target-badges">
                {notification.targetAudiences.map((aud, idx) => (
                  <span
                    key={idx}
                    className="trainer-marketing-target-badge"
                    style={{ backgroundColor: aud.color, color: 'white' }}
                  >
                    <Users size={12} />
                    {aud.name}
                  </span>
                ))}
              </div>
              <div className="trainer-marketing-audience-count">
                {notification.targetAudiences.length} audience segment{notification.targetAudiences.length > 1 ? 's' : ''} targeted
              </div>
            </div>
          </div>

          {notification.openRate && (
            <div className="trainer-marketing-modal-section">
              <div className="trainer-marketing-modal-section-header">
                <BarChart3 size={16} className="trainer-marketing-section-icon" />
                <h3>Performance Metrics</h3>
              </div>
              <div className="trainer-marketing-metrics-grid">
                <div className="trainer-marketing-metric-card">
                  <div className="trainer-marketing-metric-header">
                    <Eye size={16} className="trainer-marketing-metric-icon-blue" />
                    <span>Open Rate</span>
                  </div>
                  <div className="trainer-marketing-metric-value">{notification.openRate}%</div>
                  <div className="trainer-marketing-metric-bar">
                    <div
                      className="trainer-marketing-metric-fill trainer-marketing-metric-fill-blue"
                      style={{ width: `${notification.openRate}%` }}
                    ></div>
                  </div>
                  <div className="trainer-marketing-metric-label">Excellent engagement</div>
                </div>

                <div className="trainer-marketing-metric-card">
                  <div className="trainer-marketing-metric-header">
                    <Zap size={16} className="trainer-marketing-metric-icon-green" />
                    <span>Click Rate</span>
                  </div>
                  <div className="trainer-marketing-metric-value">{notification.clickRate}%</div>
                  <div className="trainer-marketing-metric-bar">
                    <div
                      className="trainer-marketing-metric-fill trainer-marketing-metric-fill-green"
                      style={{ width: `${notification.clickRate}%` }}
                    ></div>
                  </div>
                  <div className="trainer-marketing-metric-label">High click-through</div>
                </div>
              </div>
            </div>
          )}

          <div className="trainer-marketing-modal-section">
            <div className="trainer-marketing-modal-section-header">
              <Settings size={16} className="trainer-marketing-section-icon" />
              <h3>Delivery Details</h3>
            </div>
            <div className="trainer-marketing-delivery-grid">
              <div className="trainer-marketing-delivery-card">
                <Mail size={18} />
                <div>
                  <div className="trainer-marketing-delivery-title">Email</div>
                  <div className="trainer-marketing-delivery-status">
                    <Check size={12} />
                    Enabled
                  </div>
                </div>
              </div>
              <div className="trainer-marketing-delivery-card">
                <Bell size={18} />
                <div>
                  <div className="trainer-marketing-delivery-title">In-App</div>
                  <div className="trainer-marketing-delivery-status">
                    <Check size={12} />
                    Enabled
                  </div>
                </div>
              </div>
              <div className="trainer-marketing-delivery-card">
                <Zap size={18} />
                <div>
                  <div className="trainer-marketing-delivery-title">Both Channels</div>
                  <div className="trainer-marketing-delivery-status">
                    <Check size={12} />
                    Multi-channel
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="trainer-marketing-modal-section trainer-marketing-summary-section">
            <div className="trainer-marketing-modal-section-header">
              <Info size={16} className="trainer-marketing-section-icon" />
              <h3>Summary</h3>
            </div>
            <div className="trainer-marketing-summary-grid">
              <div className="trainer-marketing-summary-item">
                <div className="trainer-marketing-summary-label">Status</div>
                <div className="trainer-marketing-summary-value">{notification.status.charAt(0).toUpperCase() + notification.status.slice(1)}</div>
              </div>
              <div className="trainer-marketing-summary-item">
                <div className="trainer-marketing-summary-label">Sent Date</div>
                <div className="trainer-marketing-summary-value">{notification.sentDate || '-'}</div>
              </div>
              <div className="trainer-marketing-summary-item">
                <div className="trainer-marketing-summary-label">Audiences</div>
                <div className="trainer-marketing-summary-value">{notification.targetAudiences.length}</div>
              </div>
              <div className="trainer-marketing-summary-item">
                <div className="trainer-marketing-summary-label">Type</div>
                <div className="trainer-marketing-summary-value">{getTypeLabel(notification.type)}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="trainer-marketing-modal-footer">
          <button className="trainer-marketing-modal-btn trainer-marketing-modal-btn-secondary">
            <Send size={16} />
            Resend
          </button>
          <button className="trainer-marketing-modal-btn trainer-marketing-modal-btn-secondary">
            <Copy size={16} />
            Duplicate
          </button>
          <button className="trainer-marketing-modal-btn trainer-marketing-modal-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;
